import type { ItemStoreState } from "@editor/items/ItemStore";
import type { Migration } from ".";

import { transformEffectExpressionToMathExpression } from "./scripts/transformExpressionToMathExpression";

const migration: Migration = {
  applyToVersions: "<3.0.0-alpha.31",
  type: "before",
  name: "rename and transform effect expressions",
  migrate: (data: {
    flows?: ItemStoreState<FlowItem>,
    flowBranches?: ItemStoreState<FlowBranchItem>,
    indices?: ItemStoreState<IndexItem>,
    effects?: ItemStoreState<EffectItem>,
    simulations?: ItemStoreState<SimulationItem>
  }) => {
    if (data.flows) {
      for (const flow of Object.values(data.flows.items)) {
        if ("valueType" in flow) {
          flow.valueInputMode = flow.valueType;
          delete flow.valueType;
        }

        if ("valueExpression" in flow) {
          flow.valueExpression = transformEffectExpressionToMathExpression(
            flow.valueExpression,
            { affectedType: "flow", affectedId: flow.id }
          );
        }
      }
    }

    if (data.flowBranches) {
      for (const branch of Object.values(data.flowBranches.items)) {
        if ("elementEffectType" in branch) {
          branch.branchType = branch.elementEffectType;
          delete branch.elementEffectType;
        }
      }
    }

    if (data.indices) {
      for (const index of Object.values(data.indices.items)) {
        if ("valueExpression" in index) {
          index.valueExpression = transformEffectExpressionToMathExpression(
            index.valueExpression,
            { affectedType: "index", affectedId: index.id }
          );

          if (index.valueExpression.length > 0) {
            if (index.valueExpression.startsWith("tmpl_")) {
              index.valueInputMode = "template";
            } else {
              index.valueInputMode = "expression";
            }
          }
        }
      }
    }

    if (data.effects) {
      for (const effect of Object.values(data.effects.items)) {
        if ("impulseExpressions" in effect) {
          effect.impulseExpressions = effect.impulseExpressions.map(expression =>
            transformEffectExpressionToMathExpression(
              expression,
              { affectedType: effect.affectedType, affectedId: effect.affectedId }
            )
          );
        }
      }
    }

    if (data.simulations) {
      for (const simulation of Object.values(data.simulations.items)) {
        for (const roundIndex in simulation.actionPlans) {
          const actionPlan = simulation.actionPlans[roundIndex];
          for (const effect of actionPlan.scheduledEffects) {
            effect.impulseExpressions = effect.impulseExpressions.map(expression =>
              transformEffectExpressionToMathExpression(
                expression,
                { affectedType: effect.affectedType, affectedId: effect.affectedId }
              )
            );
          }
        }
      }
    }

    return data;
  }
};

type FlowItem = {
  type: "flow";
  id: number;
  uid: string;
  createdAt: number;

  valueType?: "expression" | "reference";
  valueInputMode?: "expression" | "reference";
  
  valueExpression: string;
};

type FlowBranchItem = {
  type: "flowBranch";
  id: number;
  uid: string;
  createdAt: number;
  flowId: number;

  elementEffectType?: "source" | "sink";
  branchType?: "source" | "sink";
};

type IndexItem = {
  type: "index";
  id: number;
  uid: string;
  createdAt: number;

  valueExpression: string;
  valueInputMode: "expression" | "template";
};

type EffectItem = {
  type: "effect";
  id: number;
  uid: string;
  createdAt: number;

  affectedType: "target" | "resource";
  affectedId: number;

  impulseExpressions: string[];
};

type SimulationItem = {
  type: "simulation";
  id: number;
  uid: string;
  createdAt: number;

  actionPlans: { [roundIndex: string]: ActionPlan };
};

type ActionPlan = {
  scheduledEffects: EffectItem[];
};



export default migration;
